/**
 * The SIU Triage Sentinel's Usage slice, as it reads after its recommendation is applied.
 *
 * `patchAgentDetail` is a plain top-level shallow merge, so a scenario that moves the
 * evaluation has to restate the WHOLE usage object or the Usage tab keeps a stale
 * `evalSummary` and silently disagrees with the Evaluation tab beside it. Everything here
 * is the authored bundle verbatim except the two fields that actually move: `evalSummary`
 * (82 → 90, with the dimension the recommendation lifts) and the last point of
 * `evalSpark`. Copied from the bundle rather than retyped, and token-free throughout.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

export const USAGE_SIU: AgentDetailContent['usage'] = {
  kpis: [
    {
      icon: 'M4 6h16 M4 12h16 M4 18h10 M20 17l-2 2 2 2',
      ib: 'var(--teal-tint)',
      ic: 'var(--teal)',
      delta: '+0',
      deltaColor: 'var(--ph)',
      value: '2,180',
      label: 'Queue positions watched',
      sub: 'rolling 90 days',
    },
    {
      icon: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z',
      ib: 'var(--green-tint)',
      ic: 'var(--green)',
      delta: '+4',
      deltaColor: 'var(--green)',
      value: '31',
      label: 'Cases opened',
      sub: 'each at a named officer',
    },
    {
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      ib: 'var(--red-tint)',
      ic: 'var(--red)',
      delta: '0',
      deltaColor: 'var(--green)',
      value: '0',
      label: 'Referred, cleared or re-ranked',
      sub: 'the officer decides',
    },
    {
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
      ib: 'var(--amber-tint)',
      ic: 'var(--amber-tx)',
      delta: '—',
      deltaColor: 'var(--ph)',
      value: '22d',
      label: 'Oldest unopened rank',
      sub: 'above the threshold',
    },
  ],
  weekly: [0, 0, 0, 0, 210, 340, 420, 510, 590, 640, 690, 2180],
  drivers: [
    {
      label: 'Unopened-above-threshold sweep',
      sub: 'daily',
      count: 1310,
      color: 'var(--teal)',
    },
    {
      label: 'Clearance reason audit',
      sub: 'daily',
      count: 545,
      color: 'var(--amber-tx)',
    },
    {
      label: 'Rank-versus-weight reconciliation',
      sub: 'daily',
      count: 325,
      color: 'var(--brand)',
    },
  ],
  usageApps: [
    {
      name: 'FINEOS Claims',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      calls: 1855,
    },
    {
      name: 'Claims requirement sets · Singapore',
      icon: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
      color: 'var(--brand)',
      calls: 260,
    },
    {
      name: 'Panel & provider network',
      icon: 'M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
      color: 'var(--blue-strong)',
      calls: 65,
    },
  ],
  usageSkills: [
    {
      name: 'Unopened-above-threshold sweep',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      bg: 'var(--teal-tint)',
      runs: 1310,
    },
    {
      name: 'Clearance reason audit',
      icon: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
      color: 'var(--amber-tx)',
      bg: 'var(--amber-tint)',
      runs: 545,
    },
    {
      name: 'Rank-versus-weight reconciliation',
      icon: 'M15 7a4 4 0 1 1-3.9 5H8v3H5v3H2v-3l6.1-6A4 4 0 0 1 15 7z',
      color: 'var(--blue-strong)',
      bg: 'var(--blue-tint)',
      runs: 325,
    },
    {
      name: 'Probe design & self-validation',
      icon: 'M12 2v2 M5 9h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z M9 14h.01 M15 14h.01 M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
      color: 'var(--red)',
      bg: 'var(--red-tint)',
      runs: 9,
    },
  ],
  leaders: [
    {
      init: 'AM',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      name: 'Amara Mensah',
      via: 'SIU desk',
      count: 18,
    },
    {
      init: 'TV',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      name: 'Tobias Vance',
      via: 'Claims management',
      count: 9,
    },
    {
      init: 'BC',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      name: 'Bruno Castellanos',
      via: 'Claims operations',
      count: 4,
    },
  ],
  evalSummary: {
    score: '90',
    delta: '+8',
    dims: [
      {
        name: 'Findings carrying the rank and the threshold version',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Restraint — refers, clears and re-ranks nothing',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Ageing measured from the rank, not from intake',
        score: '92',
        color: 'var(--green)',
      },
      {
        name: 'Clearances traced to a recorded reason',
        score: '84',
        color: 'var(--green)',
      },
      {
        name: 'Knows whether the referral threshold is set at the right level',
        score: '78',
        color: 'var(--green)',
      },
    ],
  },
  evalSpark: [0, 0, 0, 0, 68, 72, 74, 76, 78, 78, 80, 90],
  usersSpark: [0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 3, 3],
  cost: {
    total: '$246',
    perTask: '$0.08',
    perUser: '$82',
    segs: [
      {
        label: 'Model inference',
        amount: '$161',
        value: 161,
        color: 'var(--brand)',
      },
      {
        label: 'FINEOS Claims reads',
        amount: '$58',
        value: 58,
        color: 'var(--teal)',
      },
      {
        label: 'Rules engine reads',
        amount: '$27',
        value: 27,
        color: 'var(--blue)',
      },
    ],
  },
  heat: [
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [7, 0, 0, 0, 0, 0],
    [7, 0, 0, 0, 0, 0],
  ],
};
